///
/// EarthBrowser
///
/// Window Restoration Hook
///
/// Restores persisted window states on app launch
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { dockWindowToDrawer, openDatasetWindow, openDrawer, setActiveDrawerTab, setDrawerHeight } from '@/redux/dialogSlice'
import { constrainToViewport } from '@/utils/windowUtils'

/**
 * Hook to restore persisted window states on app launch
 *
 * Checks for persisted window states and reopens windows that were open
 * when the app was last closed. Only runs once on mount.
 *
 * Also restores bottom drawer state (height and docked windows).
 *
 * Waits for OGC sync service to be ready before restoring to prevent
 * "Database not found" errors on windows that query OGC data.
 */
export function useWindowRestoration() {
	const dispatch = useAppDispatch()
	const persistedStates = useAppSelector(state => state.userInterface.persistedWindowStates)
	const persistedDrawer = useAppSelector(state => state.userInterface.bottomDrawer)
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)
	const hasRestored = useRef(false)

	useEffect(() => {
		// Only restore once
		if (hasRestored.current) {
			return
		}

		// Wait for layer manager to be ready before restoring windows
		// This prevents "Database not found" errors on windows that query data
		if (!layerManager) {
			return
		}

		// Restore drawer height
		if (persistedDrawer?.height) {
			dispatch(setDrawerHeight(persistedDrawer.height))
		}

		// Track which windows should be docked
		const dockedWindowIds = new Set(persistedDrawer?.dockedWindowIds || [])

		// Restore each persisted window
		Object.values(persistedStates).forEach(persistedState => {
			dispatch(openDatasetWindow(constrainToViewport({
				id: persistedState.id,
				x: persistedState.x,
				y: persistedState.y,
				width: persistedState.width,
				height: persistedState.height,
				isMaximized: persistedState.isMaximized,
				isVisible: persistedState.isVisible,
				title: persistedState.title,
				windowType: persistedState.windowType,
				dataSourceId: persistedState.dataSourceId,
				catalogId: persistedState.catalogId,
				activeLayerName: persistedState.activeLayerName,
				dockMode: persistedState.dockMode,
				isDockedInDrawer: false,
			})))

			// If this window was docked, dock it to the drawer (without auto-opening)
			if (dockedWindowIds.has(persistedState.id) || persistedState.isDockedInDrawer) {
				dispatch(dockWindowToDrawer({ windowId: persistedState.id, openDrawer: false }))
			}
		})

		// Restore the selected tab (docking sets each docked window active,
		// so the last-docked one wins without this). The reducer no-ops if
		// the tab wasn't actually docked.
		if (persistedDrawer?.activeTabId) {
			dispatch(setActiveDrawerTab(persistedDrawer.activeTabId))
		}

		// Restore dock open state (docking above didn't open it). Set rather
		// than toggle so a remount (HMR) can't flip an already-open dock shut
		if (persistedDrawer?.isOpen) {
			dispatch(openDrawer())
		}

		hasRestored.current = true
	}, [dispatch, persistedStates, persistedDrawer, layerManager])
}

